/**
 * 执行航班数据选择器
 *
 * 提供执行航班的搜索、筛选和选择功能。
 * 可用于邮件发送、批量操作等需要选择航班的场景。
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Input, Table, DatePicker, Select, Space, Button, Tag, Empty, Spin, message, Tooltip, ConfigProvider, Popover, Divider, Row, Col } from "antd";
import { SearchOutlined, ReloadOutlined, FilterOutlined, InfoCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import flightOperationFlightApi from "../../../../api/flightOperationFlightApi";
import { useTimeZone, TIMEZONE_OPTIONS } from "../../../../contexts/TimeZoneContext";
import AircraftTag from "../../../../components/AircraftTag";
import FlightDateDisplay from "../../../../components/DateTimeDisplay/FlightDateDisplay";
import EnhancedFlightTimes from "../../../../features/flightOperation/Monitor/components/EnhancedFlightTimes";
import RotationVisualizer from "../../../../features/flightOperation/Monitor/components/RotationVisualizer";
import { FLIGHT_STATUS, FLIGHT_STATUS_LABELS, DEFAULT_FILTER_STATUS, FLIGHT_STATUS_COLORS } from "../../../../features/flightOperation/Monitor/constants";

import AircraftSelector from "../../../common/AircraftSelector";
import { UTCDateRangePicker } from "../../../DateTimeInput";
import { useTableSelection } from "../../../../hooks/useTableSelection";
import { DeleteOutlined } from "@ant-design/icons";

const { RangePicker } = DatePicker;
const { Option } = Select;

/**
 * 执行航班选择器组件 (Refined Version)
 */
const OperationFlightSelector = ({
    value = [],
    onChange,
    presetData,
    mode = "multiple",
    disabled = false,
    maxSelection,
    filterParams: externalFilterParams,
    defaultDateRange,
    defaultStatus,
    compact = false,
}) => {
    // ============ 状态 ============
    const { timeZone: timeZoneMode } = useTimeZone();
    const [loading, setLoading] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

    // 维护所有已选中的行数据（跨页保持）
    const [allSelectedRows, setAllSelectedRows] = useState(value || []);

    const [filters, setFilters] = useState({
        dateRange: defaultDateRange || [dayjs.utc().subtract(1, "day"), dayjs.utc().add(6, "day")],
        status: defaultStatus || DEFAULT_FILTER_STATUS,
        flightNumber: "",
        aircraft: undefined,
    });

    // ============ 数据加载 ============

    const loadFlights = useCallback(async (page = 1, pageSize = 20) => {
        setLoading(true);
        try {
            const params = {
                page,
                page_size: pageSize,
                ...externalFilterParams,
            };

            // 日期范围
            if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
                params.start_date = filters.dateRange[0].format("YYYY-MM-DD");
                params.end_date = filters.dateRange[1].format("YYYY-MM-DD");
            }

            // 航班状态
            if (filters.status && filters.status.length > 0) {
                params.status = filters.status.join(",");
            }

            // 航班号
            if (filters.flightNumber) {
                params.flight_number = filters.flightNumber;
            }

            // 飞机注册号
            if (filters.aircraft) {
                params.aircraft_reg = filters.aircraft;
            }

            const response = await flightOperationFlightApi.getFlightOperations(params);
            const data = response.data || response;

            setSearchResults(data.items || []);
            setPagination((prev) => ({
                ...prev,
                current: page,
                pageSize,
                total: data.total || 0,
            }));
        } catch (error) {
            console.error("[OperationFlightSelector] 加载航班失败:", error);
            message.error("加载航班 data 失败");
        } finally {
            setLoading(false);
        }
    }, [filters, externalFilterParams]);

    // 初始化时加载数据
    useEffect(() => {
        // 如果有预设数据，显示预设数据作为搜索结果
        if (presetData && presetData.length > 0) {
            setSearchResults(presetData);
            setPagination((prev) => ({ ...prev, total: presetData.length }));
        } else {
            loadFlights();
        }
    }, []); // 只在挂载时执行一次

    // ============ useTableSelection 集成 ============
    
    // 状态同步标志 - 避免 value -> hook -> onChange -> value 的循环更新
    const isSyncingFromProps = useRef(false);

    // 处理 Hook 内部选择变化的回调（点击、拖拽都会触发这里）
    const handleHookSelectionChange = useCallback((keys, rows) => {
        // 如果是从 props 同步触发的 Hook 更新，忽略（避免回流）
        if (isSyncingFromProps.current) {
            return;
        }
        
        // 应用 mode 和 maxSelection 限制
        let finalRows = rows;
        
        if (mode === "single" && finalRows.length > 1) {
            // 单选模式：只保留最后一个
            finalRows = [finalRows[finalRows.length - 1]];
        } else if (maxSelection && finalRows.length > maxSelection) {
            // 超出最大选择数：截断
            finalRows = finalRows.slice(0, maxSelection);
            message.warning(`最多只能选择 ${maxSelection} 条数据`);
        }
        
        // 通知父组件
        onChange && onChange(finalRows);
    }, [mode, maxSelection, onChange]);

    // 使用 useTableSelection Hook，传入 onSelectionChange 回调
    const { 
        selectedRowKeys, 
        selectedRows: hookSelectedRows, 
        tableRef, 
        rowSelection: hookRowSelection, 
        getRowProps, 
        handleRowSelectionChange, // Hook 提供的更新方法
        getIndexColumn 
    } = useTableSelection({
        rowKey: "id",
        dataSource: searchResults,
        pagination,
        onSelectionChange: handleHookSelectionChange, // 使用新的回调参数
    });

    // Effect: 同步 Props (value) -> Hook State
    // 仅用于初始化和外部控制（例如父组件清空选择、传入预设数据）
    useEffect(() => {
        const propRows = value || [];
        
        // 简单比较 ID 集合是否一致
        const currentIds = new Set(selectedRowKeys);
        const propIds = new Set(propRows.map(r => r.id));
        
        const isDifferent = 
            currentIds.size !== propIds.size || 
            [...propIds].some(id => !currentIds.has(id));

        if (isDifferent) {
            // 标记正在从 props 同步
            isSyncingFromProps.current = true;
            
            const keys = propRows.map(r => r.id);
            handleRowSelectionChange(keys, propRows);
            
            // handleRowSelectionChange 内部使用 RAF，所以我们也用 RAF 来重置 flag
            // 确保在 Hook 状态更新完成后再允许用户交互触发回调
            requestAnimationFrame(() => {
                isSyncingFromProps.current = false;
            });
        }
    }, [value]); // 仅依赖 value

    // 构建 rowSelection 配置
    const rowSelection = useMemo(() => ({
        ...hookRowSelection,
        type: mode === "single" ? "radio" : "checkbox",
        getCheckboxProps: (record) => ({
            disabled: disabled,
        }),
    }), [hookRowSelection, mode, disabled]);


    // ============ 事件处理 ============

    const handleSearch = useCallback(() => {
        loadFlights(1, pagination.pageSize);
    }, [loadFlights, pagination.pageSize]);

    const handleTableChange = useCallback(
        (newPagination) => {
            loadFlights(newPagination.current, newPagination.pageSize);
        },
        [loadFlights],
    );

    const handleFilterChange = useCallback((key, val) => {
        setFilters((prev) => ({ ...prev, [key]: val }));
    }, []);
    
    // 移除单个已选项
    const handleRemoveSelectedItem = (recordId) => {
        // 当用户点击删除标签时，基于当前的 value 进行过滤
        const currentRows = value || [];
        const newRows = currentRows.filter(r => r.id !== recordId);
        onChange && onChange(newRows);
    };
    
    // 清空所有
    const handleClearAll = () => {
        onChange && onChange([]);
    };


    // 判断航班是否存在告警 (简单逻辑，与 FlightListView 保持一致)
    const hasWarnings = useCallback((flight) => {
        return flight.status === "DELAYED";
    }, []);

    // ============ 表格列定义 ============

    const columns = useMemo(() => {
        if (compact) {
            return [
                getIndexColumn("序号", 50),
                {
                    title: "航班号",
                    dataIndex: "flight_number",
                    key: "flight_number",
                    align: "center",
                    width: 90,
                    render: (text, record) => (
                        <span style={{ fontWeight: 500 }}>
                            {hasWarnings(record) && <ExclamationCircleOutlined style={{ marginRight: 2, fontSize: "11px", color: "#faad14" }} />}
                            {text}
                        </span>
                    ),
                },
                {
                    title: "日期",
                    dataIndex: "flight_date",
                    key: "flight_date",
                    align: "center",
                    width: 90,
                    render: (flight_date, record) => {
                        const { etd, std, departure_airport } = record;
                        const primaryTime = etd || std;
                        return <FlightDateDisplay etdOrStd={primaryTime} departureAirportCode={departure_airport} originalFlightDateForFallback={flight_date} />;
                    },
                },
                {
                    title: "航段",
                    key: "route",
                    align: "center",
                    width: 110,
                    render: (_, record) => `${record.departure_airport || "-"} → ${record.arrival_airport || "-"}`,
                },
                {
                    title: "注册号",
                    dataIndex: "aircraft_reg",
                    key: "aircraft_reg",
                    align: "center",
                    width: 90,
                    render: (text) => <AircraftTag registration={text} />,
                },
                {
                    title: "STD",
                    dataIndex: "std",
                    key: "std",
                    align: "center",
                    width: 70,
                    render: (std) => std ? dayjs.utc(std).format("HH:mm") : "-",
                },
                {
                    title: "状态",
                    dataIndex: "status",
                    key: "status",
                    align: "center",
                    width: 80,
                    render: (status) => {
                        const label = FLIGHT_STATUS_LABELS[status] || status || '-';
                        const color = FLIGHT_STATUS_COLORS[status];
                        return <Tag color={color} style={{ margin: 0 }}>{label}</Tag>;
                    },
                },
            ];
        }

        return [
            getIndexColumn("序号", 60),
            {
                title: "注册号",
                dataIndex: "aircraft_reg",
                key: "aircraft_reg",
                align: "center",
                width: 70,
                render: (text) => <AircraftTag registration={text} />,
            },
            {
                title: "ROT",
                key: "rotation",
                align: "center",
                width: 40,
                render: (_, record) => <RotationVisualizer flight={record} allFlights={searchResults} />,
            },
            {
                title: "日期",
                dataIndex: "flight_date",
                key: "flight_date",
                align: "center",
                width: 60,
                render: (flight_date, record) => {
                    const { etd, std, departure_airport } = record;
                    const primaryTime = etd || std;
                    return <FlightDateDisplay etdOrStd={primaryTime} departureAirportCode={departure_airport} originalFlightDateForFallback={flight_date} />;
                },
            },
            {
                title: <Tooltip title="星期">D</Tooltip>,
                key: "day_of_week",
                align: "center",
                width: 30,
                render: (_, record) => {
                    const { etd, std, departure_airport, flight_date } = record;
                    const timeToConvert = etd || std || flight_date;

                    if (!timeToConvert) {
                        return "-";
                    }

                    try {
                        const dayOfWeek = dayjs.utc(timeToConvert).day();
                        return dayOfWeek === 0 ? 7 : dayOfWeek;
                    } catch (error) {
                        return "?";
                    }
                },
            },
            {
                title: "航班号",
                dataIndex: "flight_number",
                key: "flight_number",
                align: "center",
                width: 70,
                render: (text, record) => {
                    let flightNumberStyle = {};
                    if (timeZoneMode === TIMEZONE_OPTIONS.LOCAL) {
                        flightNumberStyle.color = "#722ed1";
                    }
                    if (hasWarnings(record)) {
                        flightNumberStyle.color = "#faad14";
                    }

                    return (
                        <span style={flightNumberStyle}>
                            {hasWarnings(record) && <ExclamationCircleOutlined style={{ marginRight: 2, fontSize: "11px", color: flightNumberStyle.color || "#faad14" }} />}
                            {text}
                        </span>
                    );
                },
            },
            {
                title: "航段",
                key: "route",
                align: "center",
                width: 80,
                render: (_, record) => `${record.departure_airport || "-"} - ${record.arrival_airport || "-"}`,
            },
            {
                title: (
                    <Tooltip title="航班时间信息">
                        航班时间 <InfoCircleOutlined style={{ fontSize: "12px" }} />
                    </Tooltip>
                ),
                key: "flight_times",
                width: 220,
                render: (_, record) => (
                    <EnhancedFlightTimes 
                        flight={record} 
                        airportsMap={{}}
                        allFlights={searchResults}
                        editable={false}
                    />
                ),
            },
        ];
    }, [searchResults, timeZoneMode, hasWarnings, getIndexColumn, compact]);

    // ============ 渲染 ============

    return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            {/* 筛选条件 - 仿照 FlightFilter 布局 */}
            <div style={{ padding: "12px 16px", borderBottom: "1px solid #f0f0f0" }}>
                <Row gutter={[12, 8]} align="top">
                    {/* 日期范围 */}
                    <Col>
                        <UTCDateRangePicker
                            size="small"
                            value={filters.dateRange}
                            onChange={(dates) => handleFilterChange("dateRange", dates)}
                            format="MM-DD"
                            style={{ width: 200 }}
                            disabled={disabled}
                        />
                    </Col>
                    
                    {/* 航班状态 */}
                    {/* 航班状态 - 加宽 */}
                    <Col>
                        <Select
                            size="small"
                            mode="multiple"
                            placeholder="航班状态"
                            value={filters.status}
                            onChange={(val) => handleFilterChange("status", val)}
                            style={{ width: 260 }}
                            disabled={disabled}
                            maxTagCount="responsive"
                            allowClear
                        >
                            {Object.values(FLIGHT_STATUS).map(status => (
                                <Option key={status} value={status}>
                                    <span style={{ color: FLIGHT_STATUS_COLORS[status] }}>
                                        {FLIGHT_STATUS_LABELS[status]}
                                    </span>
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    
                    {/* 飞机注册号 - 加宽 */}
                    <Col>
                        <AircraftSelector
                            size="small"
                            placeholder="飞机注册号"
                            value={filters.aircraft}
                            onChange={(val) => handleFilterChange("aircraft", val)}
                            style={{ width: 160 }}
                            disabled={disabled}
                            displayMode="simple"
                            allowClear
                        />
                    </Col>
                    
                    {/* 航班号 */}
                    <Col>
                        <Input
                            size="small"
                            placeholder="航班号"
                            value={filters.flightNumber}
                            onChange={(e) => handleFilterChange("flightNumber", e.target.value)}
                            style={{ width: 100 }}
                            disabled={disabled}
                            allowClear
                            onPressEnter={handleSearch}
                        />
                    </Col>
                    
                    {/* 操作按钮 */}
                    <Col>
                        <Space size="small">
                            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={loading} disabled={disabled}>
                                搜索
                            </Button>
                            <Button icon={<ReloadOutlined />} onClick={() => loadFlights()} disabled={disabled} />
                        </Space>
                    </Col>
                </Row>
            </div>

            {/* 已选内容管理栏 */}
            {value && value.length > 0 && (
                 <div style={{ 
                     padding: "8px 16px", 
                     background: "#e6f7ff", 
                     borderBottom: "1px solid #91d5ff",
                     display: "flex",
                     justifyContent: "space-between",
                     alignItems: "flex-start",
                     gap: 12
                 }}>
                     <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                            display: "flex", 
                            alignItems: "center",
                            marginBottom: 6,
                            gap: 8
                        }}>
                            <span style={{ fontWeight: 500, whiteSpace: "nowrap" }}>已选择 {value.length} 个航班</span>
                            <Button type="link" size="small" danger onClick={handleClearAll} style={{ padding: 0 }}>
                                清空
                            </Button>
                        </div>
                        {/* 显示所有选中航班，最多两行，超出部分用 Popover 展示 */}
                        <div style={{ 
                            display: 'flex', 
                            flexWrap: 'wrap',
                            gap: 4, 
                            maxHeight: 52, /* 约两行高度 */
                            overflow: 'hidden',
                            position: 'relative'
                        }}>
                             {value.slice(0, 12).map(row => (
                                 <Tag 
                                    key={row.id} 
                                    closable 
                                    onClose={() => handleRemoveSelectedItem(row.id)}
                                    color="blue"
                                    style={{ marginBottom: 0 }}
                                 >
                                     {row.flight_number}
                                 </Tag>
                             ))}
                             {value.length > 12 && (
                                 <Popover
                                     title={`全部已选航班 (${value.length})`}
                                     trigger="click"
                                     placement="bottomLeft"
                                     overlayStyle={{ maxWidth: 400 }}
                                     content={
                                         <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                                             <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                                 {value.map(row => (
                                                     <Tag 
                                                        key={row.id} 
                                                        closable 
                                                        onClose={() => handleRemoveSelectedItem(row.id)}
                                                        color="blue"
                                                     >
                                                         {row.flight_number}
                                                     </Tag>
                                                 ))}
                                             </div>
                                             <Divider style={{ margin: '8px 0' }} />
                                             <Button type="link" size="small" danger onClick={handleClearAll} style={{ padding: 0 }}>
                                                 清空所有
                                             </Button>
                                         </div>
                                     }
                                 >
                                     <Tag color="default" style={{ cursor: 'pointer', marginBottom: 0 }}>
                                         +{value.length - 12} 更多
                                     </Tag>
                                 </Popover>
                             )}
                        </div>
                     </div>
                 </div>
            )}

            {/* 数据表格 */}
            <ConfigProvider
                theme={{
                    components: {
                        Table: {
                            rowHeight: 32,
                            padding: 4,
                            paddingXS: 2,
                            paddingSM: 4,
                        },
                    },
                }}
            >
                <div style={{ flex: 1, overflow: "auto", padding: "0" }}>
                    <Table
                        size="small"
                        columns={columns}
                        dataSource={searchResults}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                            ...pagination,
                            size: "small",
                            showSizeChanger: true,
                            showTotal: (total) => `共 ${total} 条`,
                        }}
                        onChange={handleTableChange}
                        rowSelection={rowSelection} // 使用定制后的 rowSelection
                        onRow={getRowProps} // 使用 hook 提供的 getRowProps
                        scroll={{ y: 500 }}
                        locale={{
                            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无航班数据，请调整筛选条件" />,
                        }}
                        ref={tableRef}
                    />
                </div>
            </ConfigProvider>
        </div>
    );
};

export default OperationFlightSelector;
